import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { loginService, logoutService, getUsersService, deleteUserService, updateUserService } from "../Services/AuthService";
import { setAccessToken, clearAuthData } from "../Utils/localStore";

const getPaginationFromResponse = (responseData, fallbackLength = 0, perPage = 12, listSource = null) =>
  responseData?.data?.pagination ||
  listSource?.pagination ||
  (listSource?.total !== undefined ? listSource : null) ||
  responseData?.pagination ||
  responseData?.meta?.pagination ||
  responseData?.meta || {
    current_page: 1,
    per_page: perPage,
    total_items: fallbackLength,
    total_pages: Math.max(1, Math.ceil(fallbackLength / perPage)),
  };

const getListData = (...sources) => {
  for (const source of sources) {
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
  }

  return [];
};

// LOGIN THUNK
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await loginService(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || "Login failed");
    }
  },
);

// LOGOUT THUNK with API call
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      await logoutService();
      clearAuthData();
      return { success: true };
    } catch (error) {
      clearAuthData();
      return rejectWithValue(error?.message || "Logout failed");
    }
  }
);

// Get Users Thunk
export const getUsers = createAsyncThunk(
  "auth/getUsers",
  async ({ page, perPage } = {}, { rejectWithValue }) => {
    try {
      const response = await getUsersService(page, perPage);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch users");
    }
  }
);

// Delete User Thunk
export const deleteUser = createAsyncThunk(
  "auth/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await deleteUserService(userId);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to delete user");
    }
  }
);

// Update User Thunk
export const updateUser = createAsyncThunk(
  "auth/updateUser",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await updateUserService(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to update user");
    }
  }
);

const authSlice = createSlice({
  name: "auth",

  initialState: {
    users: [],
    usersPagination: {
      current_page: 1,
      per_page: 12,
      total_items: 0,
      total_pages: 1,
    },
    loading: false,
    updatingUser: false,
    deletingUser: false,
    error: null,
    isAuthenticated: false,
  },

  reducers: {
    clearAuthState: (state) => {
      state.loading = false;
      state.error = null;
    },
    setAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
    },
    clearUsers: (state) => {
      state.users = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---------------- LOGIN CASES ----------------
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.isAuthenticated = true;
        
        const token = action.payload?.data?.access_token || action.payload?.data?.token || action.payload?.access_token;
        if (token) {
          setAccessToken(token);
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      
      // ---------------- LOGOUT CASES ----------------
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = null;
      })

      // Get Users Cases
      .addCase(getUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        
        if (responseData?.success && responseData?.data) {
          state.users = getListData(responseData.data.users);
          state.usersPagination = getPaginationFromResponse(
            responseData,
            state.users.length,
            action.meta.arg?.perPage || 12,
            responseData.data.users
          );
        } else if (Array.isArray(responseData)) {
          state.users = responseData;
          state.usersPagination = getPaginationFromResponse(
            responseData,
            responseData.length,
            action.meta.arg?.perPage || 12
          );
        } else {
          state.users = [];
          state.usersPagination = getPaginationFromResponse(
            responseData,
            0,
            action.meta.arg?.perPage || 12
          );
        }
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.users = [];
      })
      
      // Delete User Cases
      .addCase(deleteUser.pending, (state) => {
        state.deletingUser = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.deletingUser = false;
        const deletedUserId = action.meta.arg;
        state.users = state.users.filter(user => user.id !== deletedUserId);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deletingUser = false;
        state.error = action.payload;
      })
      
      // Update User Cases
      .addCase(updateUser.pending, (state) => {
        state.updatingUser = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updatingUser = false;
        const updatedUser = action.payload?.data?.user || action.payload?.data;
        if (updatedUser) {
          const index = state.users.findIndex(user => user.id === updatedUser.id);
          if (index !== -1) {
            state.users[index] = { ...state.users[index], ...updatedUser };
          }
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.updatingUser = false;
        state.error = action.payload;
      });
  },
});

export const { clearAuthState, setAuthenticated, clearUsers } = authSlice.actions;
export default authSlice.reducer;
